/**
 * ThemeSwitcher — the in-app theme picker (same 15 themes + behaviour as the
 * Login screen).  Dropped in the app-shell top bar so users can recolour the
 * whole application live without logging out.
 *
 * Picking a theme calls applyAppTheme(): it writes the --brand-* CSS variables
 * to :root and persists the choice to localStorage, so every shared surface
 * (grid header/hover/selection, toolbar, popup headers, primary buttons —
 * all driven by --brand-accent) updates immediately and survives a refresh.
 */
import { useState, useRef, useEffect } from 'react';
import { THEMES } from '../../features/auth/themes';
import { applyAppTheme, useAppTheme } from '../../theme/useAppTheme';
import { api } from '../../api/client';
import './themeSwitcher.css';

/** Persist the user's theme so it follows them to any device on next login
 *  (dbo.AI_AppSettings key UserTheme:{oid}). Fire-and-forget: a save hiccup
 *  never blocks the instant local theme change. */
function saveUserTheme(themeId: string): void {
  void api.put('/api/v1/app-config/user-theme', { theme: themeId },
    { silentLoading: true } as never).catch(() => { /* non-fatal */ });
}

export function ThemeSwitcher() {
  const theme = useAppTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div className="ts-wrap" ref={ref}>
      <button
        className="tb-btn ts-trigger ts-icon-only"
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={`Theme: ${theme.name} — click to change`}
        aria-label={`Theme: ${theme.name}. Click to change`}
      >
        {/* Legacy theme symbol (2026-09-18) — the EXACT ALPACA LIMS (XAF) "Choose
            Theme" action icon, `Action_ChooseSkin_32x32` extracted from
            DevExpress.ExpressApp.Images.v20.1.dll, shown ICON-ONLY like the legacy
            menu-bar button: no bordered box, no theme-name text — just the icon +
            caret. The current theme name lives in the tooltip (title/aria-label). */}
        <img className="ts-cur-ico" src={`${import.meta.env.BASE_URL}legacy-nav-icons/theme-choose.png`} alt="" aria-hidden="true" />
        <i className={`ti ti-chevron-down ts-chev${open ? ' open' : ''}`} />
      </button>
      {open && (
        <div className="ts-menu" role="menu">
          {THEMES.map((t) => (
            <button
              key={t.id}
              type="button"
              role="menuitem"
              className={`ts-opt${t.id === theme.id ? ' active' : ''}`}
              onClick={() => { applyAppTheme(t); saveUserTheme(t.id); setOpen(false); }}
            >
              <span className="ts-sw" style={{ background: t.accent }} />
              {t.name}
              {t.id === theme.id && <i className="ti ti-check ts-tick" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
